import { ActionIcon, Button, Menu, Progress, Skeleton, Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import type { RunStatus } from '@prisma/client';
import { IconDots, IconExclamationCircle, IconExternalLink, IconPlayerPlay, IconTrash } from '@tabler/icons-react';
import type { ColumnDef, Row } from '@tanstack/react-table';
import Link from 'next/link';
import { typedEntries } from '~/common/object/typed-entries';
import { cn } from '~/common/string/classname';
import { GitBranchBadge } from '~/components/common/GitBranchBadge';
import { RunResultOverviewIcons } from '~/components/history/RunResultOverviewIcons';
import ManualTriggerAdvanced from '~/components/testSuite/ManualTriggerAdvanced';
import { ProgressSection, getStatusColor } from '~/components/testSuite/TestSuiteResultView';
import { useProjectId } from '~/hooks/context/useProjectId';
import { useSuiteAddTests } from '~/hooks/testSuite/useSuiteAddTests';
import { api, type RouterOutputs } from '~/trpc-extra/api';
import RunStatusBadge from '../../testSuite/RunStatusBadge';
import TimeAgo from '../../time/TimeAgo';

type TestSuiteListWithLatestRunItem = RouterOutputs['testSuite']['listWithLatestRun'][number];

function ActionsCell({ row }: { row: Row<TestSuiteListWithLatestRunItem> }) {
  const utils = api.useUtils();
  const projectId = useProjectId();

  const { mutate: deleteTestSuite } = api.testSuite.delete.useMutation({
    onMutate: () => {
      notifications.show({
        color: 'gray',
        title: 'Deleting test suite',
        message: 'Please wait...',
        icon: <IconTrash size="1rem" />,
      });
    },
    onSuccess: async () => {
      notifications.show({
        color: 'teal',
        title: 'Complete',
        message: 'Test Suite Deleted',
        icon: <IconTrash size="1rem" />,
      });
      await utils.testSuite.listWithLatestRun.invalidate();
    },
    onError: () => {
      notifications.show({
        color: 'red',
        title: 'Error',
        message: 'Issue while deleting',
        icon: <IconExclamationCircle size="1rem" />,
      });
    },
  });

  return (
    <Menu
      shadow="md"
      width={175}
      transitionProps={{ duration: 0 }}
    >
      <Menu.Target>
        <ActionIcon
          variant="subtle"
          color="gray"
          onClick={(e) => e.stopPropagation()}
        >
          <IconDots size={14} />
        </ActionIcon>
      </Menu.Target>
      <Menu.Dropdown onClick={(e) => e.stopPropagation()}>
        <Menu.Label>Quick Actions</Menu.Label>
        <Menu.Item
          leftSection={<IconExternalLink size={14} />}
          component={Link}
          href={`/project/${projectId}/testSuite/${row.original.id}`}
        >
          View Details
        </Menu.Item>
        {row.original._count.suiteTests > 0 && (
          <Menu.Item
            leftSection={<IconPlayerPlay size={14} />}
            onClick={() => {
              modals.open({
                title: 'Manual Trigger',
                size: '60%',
                children: <ManualTriggerAdvanced testSuiteId={row.original.id} />,
              });
            }}
          >
            Run Suite
          </Menu.Item>
        )}
        <Menu.Divider />
        <Menu.Item
          color="red"
          leftSection={<IconTrash size={14} />}
          onClick={() =>
            modals.openConfirmModal({
              title: `Delete Test Suite?`,
              children: (
                <Text>
                  This will permanently delete <span className="font-bold">{row.original.name}</span>
                </Text>
              ),
              labels: { confirm: 'Delete', cancel: 'Cancel' },
              onConfirm: () => deleteTestSuite({ projectId: projectId!, testSuiteId: row.original.id }),
              confirmProps: { color: 'red' },
              centered: true,
              size: 'xs',
            })
          }
        >
          Delete
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
}

function RunButton({ row }: { row: Row<TestSuiteListWithLatestRunItem> }) {
  const projectId = useProjectId();
  const { data: tests = [] } = api.test.listTests.useQuery({
    projectId: projectId!,
  });

  const { addTestsToSuiteHandler } = useSuiteAddTests({
    tests,
    testSuite: {
      ...row.original,
      suiteTests: [], // workaround to avoid type error. we have "listWithLatestRun" and "getTestSuite" that return different types... should be refactored
    },
  });

  return (
    <Button
      variant="light"
      size="xs"
      className="w-[100px]"
      onClick={() => {
        if (row.original._count.suiteTests > 0) {
          modals.open({
            title: 'Manual Trigger',
            size: '60%',
            children: <ManualTriggerAdvanced testSuiteId={row.original.id} />,
          });
        } else {
          addTestsToSuiteHandler();
        }
      }}
    >
      {row.original._count.suiteTests > 0 ? 'Run' : 'Add Tests'}
    </Button>
  );
}

export const testSuiteColumns: ColumnDef<TestSuiteListWithLatestRunItem>[] = [
  {
    accessorFn: (row) => row.name,
    id: 'name',
    size: 250,
    header: 'Suite Name',
    enableHiding: false,
    cell: ({ row, table }) => {
      const disableNavigation = !table.options.meta?.enableNavigation;
      return (
        <Link
          href={disableNavigation ? '#' : `/project/${row.original.projectId}/testSuite/${row.original.id}`}
          className={cn({
            'h-full font-medium text-gray-800 hover:text-blue-600 dark:text-gray-200 dark:hover:text-blue-400':
              !disableNavigation,
          })}
        >
          {row.original.name}
        </Link>
      );
    },
    meta: {
      skeleton: () => (
        <Skeleton
          height={20}
          width={`${Math.random() * 40 + 200}px`}
        />
      ),
    },
  },
  {
    accessorFn: (row) => row._count.suiteTests,
    id: 'testCount',
    header: 'Tests',
    size: 75,
    cell: ({ row }) => <Text className="text-gray-700 dark:text-gray-300">{row.original._count?.suiteTests || 0}</Text>,
    meta: {
      skeleton: () => (
        <Skeleton
          height={20}
          width={`${Math.random() * 10 + 30}px`}
        />
      ),
    },
  },
  {
    accessorFn: (row) => row.latestSuiteRun?.status,
    id: 'latestRunStatus',
    header: 'Last Run Status',
    size: 240,
    cell: ({ row }) => {
      const { latestSuiteRun } = row.original;
      const testRuns = latestSuiteRun?.testRuns?.filter((run) => run.status !== 'SKIPPED') ?? [];
      const statusGroups = Object.groupBy(
        testRuns.filter((run) => run.status !== undefined && run.status !== null),
        (run) => run.status,
      );

      const passNum = testRuns.filter((run) => run.status === 'PASSED').length;
      const flakyNum = testRuns.filter((run) => run.status === 'FLAKY').length;
      const failNum = testRuns.filter((run) => run.status === 'FAILED').length;
      const runningNum = testRuns.filter((run) => run.status === 'RUNNING').length;

      return (
        <div className="mr-4 space-y-1">
          <div className="flex items-end justify-between">
            <RunStatusBadge status={latestSuiteRun?.status} />
            <div className="flex items-center gap-2">
              <RunResultOverviewIcons
                status="PASSED"
                value={passNum + flakyNum}
              />
              <RunResultOverviewIcons
                status="FAILED"
                value={failNum}
              />
              <RunResultOverviewIcons
                status="RUNNING"
                value={runningNum}
              />
            </div>
          </div>
          <Progress.Root size="xs">
            {typedEntries(statusGroups)
              .sort(([statusA], [statusB]) => {
                const statusOrder: RunStatus[] = ['PASSED', 'FLAKY', 'FAILED', 'RUNNING', 'QUEUED', 'CANCELLED'];
                const indexA = statusOrder.indexOf(statusA);
                const indexB = statusOrder.indexOf(statusB);
                return indexA - indexB;
              })
              .filter(([, runs]) => runs.length > 0)
              .map(([status, runs]) => (
                <ProgressSection
                  key={status}
                  label={status}
                  color={getStatusColor({ status, colorScheme: 'light' })}
                  value={runs.length}
                  total={testRuns.length}
                />
              ))}
          </Progress.Root>
        </div>
      );
    },
    sortingFn: (rowA, rowB) => {
      const a = rowA.original.latestSuiteRun?.status;
      const b = rowB.original.latestSuiteRun?.status;
      if (a === b) {
        return 0;
      }
      return a === 'PASSED' ? -1 : 1;
    },
    filterFn: (row, id, filterValue) => {
      const status = row.original.latestSuiteRun?.status?.toLowerCase();
      return status?.includes(filterValue.toLowerCase()) ?? false;
    },
    enableColumnFilter: true,
    meta: {
      skeleton: () => (
        <Skeleton
          height={20}
          width={`${Math.random() * 40 + 200}px`}
        />
      ),
    },
  },
  {
    accessorFn: (row) => row.latestSuiteRun?.gitBranch,
    id: 'gitBranch',
    header: 'Git Branch',
    size: 120,
    enableColumnFilter: true,
    cell: ({ row }) => {
      const gitBranch = row.original.latestSuiteRun?.gitBranch;
      return gitBranch && <GitBranchBadge gitBranch={gitBranch} />;
    },
    meta: {
      skeleton: () => (
        <Skeleton
          height={20}
          radius="xl"
          width={`${Math.random() * 40 + 50}px`}
          hidden={Math.random() > 0.5}
        />
      ),
    },
  },
  {
    accessorFn: (row) => row.latestSuiteRun?.createdAt,
    id: 'latestRunTime',
    header: 'Last Ran',
    sortingFn: 'datetime',
    sortUndefined: 'last',
    invertSorting: true,
    cell: ({ row }) =>
      row.original.latestSuiteRun?.createdAt ?
        <TimeAgo
          time={row.original.latestSuiteRun.createdAt}
          textStyle="text-[var(--mantine-color-dimmed)]"
        />
      : <Text
          span
          c="dimmed"
          size="sm"
        >
          N/A
        </Text>,
    meta: {
      skeleton: () => (
        <Skeleton
          height={20}
          width={`${Math.random() * 40 + 80}px`}
        />
      ),
    },
  },
  {
    id: 'runButton',
    enableHiding: false,
    enableSorting: false,
    size: 120,
    cell: ({ row }) => <RunButton row={row} />,
  },
  {
    id: 'actions',
    enableHiding: false,
    enableSorting: false,
    header: () => <div role="none" />,
    cell: ({ row }) => <ActionsCell row={row} />,
  },
];
